import { FormatTimeAs12Hour } from 'core/Utilities'
import { type AttendanceDashboardSessionDto, type AttendanceStatusTone } from 'Pages/Attendance/types'

export const allBlocksValue = 'all-blocks'
export const allFacilitiesValue = 'all-facilities'
export const allStaffValue = 'all-staff'
export const unassignedStaffValue = 'unassigned-staff'
export const unassignedStaffLabel = 'Unassigned staff'
export const attendanceDayLabels = ['Su', 'M', 'T', 'W', 'Th', 'F', 'Sa']

export interface AttendanceFilterSelectOption {
  value: string
  label: string
}

export function currentWeekRange (date: Date): { from: string, to: string } {
  const start = new Date(date)
  const diff = (start.getDay() + 6) % 7
  start.setDate(start.getDate() - diff)
  const end = new Date(start)
  end.setDate(start.getDate() + 4)
  return { from: toDateInputValue(start), to: toDateInputValue(end) }
}

export function toDateInputValue (date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function uniqueOptions<T> (items: T[], map: (item: T) => AttendanceFilterSelectOption): AttendanceFilterSelectOption[] {
  const options = new Map<string, string>()
  items.forEach(item => {
    const option = map(item)
    options.set(option.value, option.label)
  })
  return Array.from(options.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function optionValue (id: number | null | undefined, fallbackLabel: string): AttendanceFilterSelectOption {
  return { value: id == null ? fallbackLabel : String(id), label: fallbackLabel }
}

export function staffFilterOptions (session: AttendanceDashboardSessionDto): AttendanceFilterSelectOption[] {
  if (session.staffMembers.length === 0) {
    return [{ value: unassignedStaffValue, label: unassignedStaffLabel }]
  }

  return session.staffMembers.map(staff => ({ value: String(staff.staffMemberId), label: staff.staffName }))
}

export function staffLabel (session: AttendanceDashboardSessionDto): string {
  if (session.staffMembers.length === 0) {
    return unassignedStaffLabel
  }

  return session.staffMembers.map(staff => staff.staffName).join(', ')
}

export function sessionStatus (session: AttendanceDashboardSessionDto): { label: string, tone: AttendanceStatusTone } {
  switch (session.status) {
    case 'Complete':
      return { label: 'All Caught Up', tone: 'success' }
    case 'NotStarted':
      return { label: 'Session Not Started', tone: 'neutral' }
    case 'Incomplete':
    case 'UnknownMissingCount':
      return { label: 'Incomplete', tone: 'warning' }
  }

  return { label: 'Incomplete', tone: 'warning' }
}

export function rateLabel (rate?: number | null): string {
  if (rate == null) {
    return 'No Data'
  }

  return `${Math.round(rate * 100)}%`
}

export function scheduleLabel (scheduledDays: Array<string | number>): string {
  const labels = activeDayIndexes(scheduledDays).map(day => attendanceDayLabels[day])
  return labels.length === 0 ? 'No scheduled days' : labels.join(', ')
}

export function activeDayIndexes (scheduledDays: Array<string | number>): number[] {
  const dayIndexes: Record<string, number> = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6
  }

  return scheduledDays
    .map(day => typeof day === 'number' ? day : dayIndexes[day])
    .filter(day => Number.isInteger(day) && day >= 0 && day <= 6)
    .filter((day): day is number => day !== undefined)
}

export function dateRangeLabel (startDate?: string | null, endDate?: string | null): string | undefined {
  if (startDate == null && endDate == null) {
    return 'No date range set'
  }

  if (startDate != null && endDate != null) {
    return `${formatDate(startDate)} – ${formatDate(endDate)}`
  }

  if (startDate != null) {
    return `${formatDate(startDate)} – No end date`
  }

  return `No start date – ${formatDate(endDate ?? '')}`
}

export function timeLabel (startTime?: string | null, endTime?: string | null): string | undefined {
  if (startTime == null || endTime == null) {
    return undefined
  }

  return `${FormatTimeAs12Hour(startTime)} - ${FormatTimeAs12Hour(endTime)}`
}

function formatDate (value: string): string {
  const parsed = parseDateOnly(value)
  return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

function parseDateOnly (value: string): Date {
  const [year, month, day] = value.substring(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}
